import React, { useMemo } from "react"

import { useTranslation } from "react-i18next"
import { useParams } from "react-router-dom"
import { useStoreApi } from "reactflow"

import { Grid } from "@mui/material"

import { type MenuOptions, SelectWithSearch } from "../../../../components/UI/SelectWithSearch"
import type { DeploymentList } from "../../../../types/generated/api/DeploymentList"
import type { PaginatedCalculatorListList } from "../../../../types/generated/api/PaginatedCalculatorListList"
import type { PaginatedDeploymentListList } from "../../../../types/generated/api/PaginatedDeploymentListList"
import type { PaginatedRuleSetListList } from "../../../../types/generated/api/PaginatedRuleSetListList"
import type { PaginatedScriptGroupListList } from "../../../../types/generated/api/PaginatedScriptGroupListList"
import { RuleSetCreateRequest } from "../../../../types/generated/api/RuleSetCreateRequest"
import type { RuleSetList } from "../../../../types/generated/api/RuleSetList"
import { DataKeyName, DropdownTitle, type PaginatedListType, type SelectDataMenuProps } from "../../types/interfaces"
import { getTrimmedComputedFeatureName } from "../../utils/workflowHelpers"

/**
 * Shared SelectDataMenu component that provides a dropdown for selecting data items.
 * Handles different data types and updates node state accordingly.
 */
export function SelectDataMenu(props: Readonly<SelectDataMenuProps>): React.ReactElement {
  const { menuView, nodeId, dataItemId, itemData, dataType, setSelectedValue, isDataLoading } = props

  const { id: projectId } = useParams<{ id: string }>()

  const store = useStoreApi()

  const { t } = useTranslation("workflows", {
    keyPrefix: "selectionNode",
  })

  // extracting and memoizing the GENERIC and DECISION typed rulesets
  const NodeDataMemo = useMemo(() => {
    const rulesetData = itemData as PaginatedRuleSetListList

    switch (dataType) {
      case "RulesetNode":
        return rulesetData?.results?.filter((data: RuleSetList) => data?.type === RuleSetCreateRequest.type.GENERIC)
      case "DecisionNode":
        return rulesetData?.results?.filter(
          (ruleset: RuleSetList) => ruleset?.type === RuleSetCreateRequest.type.DECISION,
        )
      case "ProjectNode":
        return (itemData as PaginatedDeploymentListList)?.results
          ?.filter((project) => project?.uuid !== projectId)
          ?.filter((project) => project.category === "model")
      case "CalculatorNode":
        return (itemData as PaginatedCalculatorListList)?.results
      case "ScriptNode":
        return (itemData as PaginatedScriptGroupListList)?.results

      default:
        return (itemData as PaginatedListType)?.results
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataType, (itemData as PaginatedListType)?.results, itemData])

  const onChange = (item: string): void => {
    setSelectedValue(item)
    const { getNodes, setNodes } = store.getState()
    let trimmedName: string | undefined = ""

    if (["ProjectNode", "ProgramNode", "ScorecardsetNode"].includes(dataType)) {
      if (dataType === "ProjectNode") {
        trimmedName = getTrimmedComputedFeatureName(
          (itemData as PaginatedDeploymentListList).results,
          item as keyof typeof DeploymentList,
          "uuid",
        )
      } else {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        trimmedName = getTrimmedComputedFeatureName((itemData as any)?.results, item, "uuid")
      }
    }

    setNodes(
      getNodes().map((node) => {
        const newNode = { ...node }
        if (node.id === nodeId && dataType) {
          if (["ProjectNode", "ProgramNode", "ScorecardsetNode"].includes(dataType)) {
            newNode.data = {
              ...newNode.data,
              [DataKeyName[dataType as keyof typeof DropdownTitle]]: item,
              computed_feature_name: trimmedName,
            }
          } else if (dataType === "CalculatorNode") {
            newNode.data = {
              ...newNode.data,
              calculator: item,
            }
          } else {
            newNode.data = {
              ...newNode.data,
              [DataKeyName[dataType as keyof typeof DropdownTitle]]: item,
            }
          }
        }
        return newNode
      }),
    )
  }

  const getMenuOptions = (): Array<MenuOptions> => {
    return (
      NodeDataMemo?.map((node) => ({
        label: node?.name,
        value: node?.uuid,
      })) ?? []
    )
  }

  const getOptionName = (resourceId: string): string => {
    return NodeDataMemo?.find((item) => item?.uuid === resourceId)?.name as string
  }

  return (
    <Grid item xs={12} className="disableKeyboardA11y">
      <SelectWithSearch
        isDropDownDisabled={
          menuView ??
          (dataType === "ProjectNode"
            ? !(itemData as PaginatedDeploymentListList)?.count
            : !(itemData as PaginatedListType)?.results?.length || !NodeDataMemo?.length)
        }
        options={[...getMenuOptions()]}
        placeHolder={
          dataType === "StartNode"
            ? ""
            : t(`dropdownPlaceholders.${dataType as keyof typeof DropdownTitle}`, {
                defaultValue: DropdownTitle[dataType as keyof typeof DropdownTitle],
              })
        }
        initialValue={
          typeof dataItemId !== "string" && dataItemId?.name ? dataItemId?.name : getOptionName(dataItemId as string)
        }
        searchInputPlaceHolder={t("searchPlaceholder")}
        isOptionsLoading={isDataLoading}
        fullWidth
        onSelectMenuItem={(item: { label: string; value: string }) => onChange(item?.value)}
        key={nodeId ?? "0"}
      />
    </Grid>
  )
}
